import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  sendChatToIA, sendChatToIAStream,
  fetchIAUserLogs, downloadIAXlsx, sendIAFeedback,
  fetchConversas, createConversa, fetchConversa,
  renameConversa, deleteConversa,
} from '../../services/api';
import { FiSend, FiUser, FiDownload, FiClock, FiFileText, FiThumbsUp, FiThumbsDown, FiPlus, FiEdit2, FiTrash2, FiMessageSquare, FiMenu, FiX } from 'react-icons/fi';

// ── Robô avatar da IA ────────────────────────────────────────────────────────
const RoboAvatar = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Antena */}
    <rect x="30" y="2" width="4" height="10" rx="2" fill="white" opacity="0.9"/>
    <circle cx="32" cy="2" r="3" fill="#7dd3fc"/>
    {/* Cabeça */}
    <rect x="10" y="12" width="44" height="34" rx="10" fill="white" opacity="0.95"/>
    {/* Olhos - esquerdo */}
    <rect x="17" y="22" width="12" height="10" rx="4" fill="#0ea5e9"/>
    <circle cx="23" cy="27" r="3" fill="white" opacity="0.9"/>
    <circle cx="24" cy="26" r="1.2" fill="#0ea5e9"/>
    {/* Olhos - direito */}
    <rect x="35" y="22" width="12" height="10" rx="4" fill="#0ea5e9"/>
    <circle cx="41" cy="27" r="3" fill="white" opacity="0.9"/>
    <circle cx="42" cy="26" r="1.2" fill="#0ea5e9"/>
    {/* Boca */}
    <rect x="22" y="36" width="20" height="5" rx="2.5" fill="#bae6fd" opacity="0.8"/>
    <rect x="25" y="37" width="4" height="3" rx="1" fill="white" opacity="0.6"/>
    <rect x="30" y="37" width="4" height="3" rx="1" fill="white" opacity="0.6"/>
    <rect x="35" y="37" width="4" height="3" rx="1" fill="white" opacity="0.6"/>
    {/* Pescoço */}
    <rect x="27" y="46" width="10" height="6" rx="3" fill="white" opacity="0.7"/>
    {/* Corpo */}
    <rect x="14" y="52" width="36" height="10" rx="5" fill="white" opacity="0.8"/>
  </svg>
);
import { Offcanvas } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import styles from './ChatAssistant.module.css';

// ─── Formatação de valores monetários nas tabelas ───────────────────────────

const MONEY_EXCLUDE = /quantidade|qtd|count|percentual|pct|_pct|var_|dias|_dias|id$|codigo|score|ranking|mes$|ano$|status|tier|uf$|loja$|pedidos$|pedido$|itens$|item$|clientes$|cliente$|produtos$|produto$|novos$|unicos$|recorrentes$|_num$|_qt$/i;

// Headers genéricos que NÃO devem ser tratados como monetários sozinhos
// "Valor" sozinho é ambíguo (pode ser data, quantidade, etc.)
const MONEY_AMBIGUOUS = /^valor$/i;

const MONEY_PATTERNS = [
  /^(valor\s+\w|preco|frete|custo|receita|faturamento|ticket|desconto|ltv|lucro|bruto|liquido|meta|gasto|investimento)/i,
  /^total(?!_pedidos|_itens|_clientes|_produtos|_novos|_unicos|_recorrentes)/i,
  /(valor|preco|frete|custo|receita|ticket|desconto|ltv|lucro|bruto|liquido|_brl|_reais)$/i,
  /\(r\$\)/i, // Header contém "(R$)" → sempre monetário
];

const isColMoney = (col) => {
  if (!col) return false;
  const c = col.trim();
  if (MONEY_EXCLUDE.test(c)) return false;
  // "Valor" sozinho é ambíguo — não formatar automaticamente
  if (MONEY_AMBIGUOUS.test(c)) return false;
  return MONEY_PATTERNS.some(p => p.test(c));
};

// Detecta se o conteúdo NÃO é monetário (datas, unidades, texto)
const VALUE_NOT_MONEY = /[\/]|\d{4}-\d{2}|\b(dias?|unidades?|meses?|horas?|itens?|und|pcs?|kg|mm|cm)\b/i;

const fmtBRL = (val) => {
  if (val == null || val === '' || val === '-') return val;
  // Se já está formatado (contém R$) não reformata
  if (typeof val === 'string' && val.includes('R$')) return val;
  // Se parece data (contém /) ou contém unidade textual → não formatar
  const s = String(val).trim();
  if (VALUE_NOT_MONEY.test(s)) return val;
  const n = Number(s.replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (isNaN(n)) return val;
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ─────────────────────────────────────────────────────────────────────────────

// Baixar Tabela
const exportTableToXLSX = (tableText) => {
  const lines = tableText.trim().split('\n');
  const rows = lines.map(line => line.split('|').map(cell => cell.trim()).filter(cell => cell !== ""))
      .filter((row, idx) => idx !== 1 && row.length > 0);

  if (rows.length > 0) {
      const ws = XLSX.utils.aoa_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dados IA");
      XLSX.writeFile(wb, `IA_Relatorio_${Date.now()}.xlsx`);
  }
};

const exportJsonToXLSX = (jsonString) => {
    try {
      const data = JSON.parse(jsonString);
      if (!Array.isArray(data) || data.length === 0) return;
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dados IA Completo");
      XLSX.writeFile(wb, `MegaLista_IA_${Date.now()}.xlsx`);
    } catch(e) { console.error('Erro ao gerar XLSX JSON', e); }
};

const parseMarkdownTable = (tableString) => {
  const lines = tableString.trim().split('\n').filter(l => l.trim() !== '');
  if (lines.length < 3) return null;

  const headers = lines[0].split('|').map(h => h.trim()).filter(h => h !== '');
  const bodyRows = lines.slice(2).map(l => l.split('|').map(c => c.trim()).filter(c => c !== ''));
  const moneyFlags = headers.map(h => isColMoney(h.replace(/\*\*/g, '')));

  return (
    <div className="table-responsive my-3" style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <table className="table table-light table-striped table-sm table-bordered shadow-sm" style={{ fontSize: '0.85rem' }}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i}>{h.replace(/\*\*/g, '')}</th>)}</tr>
        </thead>
        <tbody>
          {bodyRows.map((row, i) => (
            <tr key={i}>{row.map((cell, j) => {
              const raw = cell.replace(/\*\*/g, '');
              return <td key={j} style={moneyFlags[j] ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}}>{moneyFlags[j] ? fmtBRL(raw) : raw}</td>;
            })}</tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={() => exportTableToXLSX(tableString)}
        className="btn btn-sm btn-success mt-1 d-flex gap-2 align-items-center"
        style={{ fontSize: '0.75rem', fontWeight: '500' }}
        title="Baixar planilha Excel"
      >
        <FiDownload /> Baixar Relatório XLSX
      </button>
    </div>
  );
};

const parseJsonTable = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data) || data.length === 0) return null;
    const headers = Object.keys(data[0]);
    const moneyFlags = headers.map(h => isColMoney(h));
    return (
      <div className="table-responsive my-3" style={{ maxWidth: '100%', overflowX: 'auto', maxHeight: '400px' }}>
        <table className="table table-dark table-striped table-sm table-bordered shadow-sm" style={{ fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#212529' }}>
            <tr>{headers.map((h, i) => <th key={i}>{h.toUpperCase().replace(/_/g, ' ')}</th>)}</tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>{headers.map((h, j) => (
                <td key={j} style={moneyFlags[j] ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}}>
                  {moneyFlags[j] ? fmtBRL(row[h]) : row[h]}
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
        <div className="d-flex justify-content-between align-items-center mt-2 p-1 bg-dark rounded">
           <span className="text-light" style={{fontSize: '0.75rem'}}>{data.length} linhas agrupadas com sucesso</span>
           <button
             onClick={() => exportJsonToXLSX(jsonString)}
             className="btn btn-sm btn-success d-flex gap-2 align-items-center"
             style={{ fontSize: '0.75rem', fontWeight: '500' }}
             title="Baixar planilha Excel Completa Oculta"
           >
             <FiDownload /> Baixar Tabela Completa ({data.length})
           </button>
        </div>
      </div>
    );
  } catch(e) {
      return <pre className="bg-dark text-danger p-2 rounded" style={{fontSize:'0.8rem'}}>Erro ao construir tabela da Mega-Lista: Dados inválidos.</pre>;
  }
};

// ── Grid de imagens de produtos ─────────────────────────────────────────────
const parseImagesBlock = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data) || data.length === 0) return null;

    const formatBRL = (v) =>
      v != null && !isNaN(Number(v))
        ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : null;

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '12px 0' }}>
        {data.map((item, i) => {
          const imgSrc = item.imagem_url || item.imagem_base64 || item.imagem || null;
          const nome = item.descricao || item.nome || item.produto || '—';
          const preco = formatBRL(item.preco_venda ?? item.preco ?? item.valor_unitario ?? null);
          const estoque = item.estoque != null ? `Estoque: ${item.estoque}` : null;
          const categoria = item.categoria || null;
          const codigo = item.codigo_interno || item.codigo || item.codigo_produto || null;

          return (
            <div
              key={i}
              style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '10px',
                width: '150px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={nome}
                  style={{ width: '100%', height: '130px', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div
                  style={{
                    width: '100%', height: '130px', background: '#0f172a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '28px', color: '#475569',
                  }}
                >
                  💎
                </div>
              )}
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <span style={{ fontSize: '0.72rem', color: '#e2e8f0', fontWeight: 600, lineHeight: 1.3 }}>
                  {nome.length > 55 ? nome.substring(0, 55) + '…' : nome}
                </span>
                {codigo && (
                  <span style={{ fontSize: '0.65rem', color: '#64748b' }}>{codigo}</span>
                )}
                {categoria && (
                  <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic' }}>{categoria}</span>
                )}
                {preco && (
                  <span style={{ fontSize: '0.75rem', color: '#4ade80', fontWeight: 700 }}>{preco}</span>
                )}
                {estoque && (
                  <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{estoque}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  } catch (e) {
    return null;
  }
};

const FormattedMessage = ({ text }) => {
  if (!text) return null;

  const blocks = [];
  const lines = text.split('\n');
  let currentTable = [];
  let currentText = [];

  let inJsonBlock = false;
  let currentJson = [];
  let inImagesBlock = false;
  let currentImages = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Bloco de imagens de produtos
    if (line.trim().startsWith('```images')) {
        if (currentText.length > 0) { blocks.push({ type: 'text', content: currentText.join('\n') }); currentText = []; }
        if (currentTable.length > 0) { blocks.push({ type: 'table', content: currentTable.join('\n') }); currentTable = []; }
        inImagesBlock = true;
        continue;
    }
    if (inImagesBlock && line.trim().startsWith('```')) {
        inImagesBlock = false;
        if (currentImages.length > 0) { blocks.push({ type: 'images', content: currentImages.join('\n') }); currentImages = []; }
        continue;
    }
    if (inImagesBlock) { currentImages.push(line); continue; }

    // Tratamento de Bloco JSON Oculto (Megalist Renderer)
    if (line.trim().startsWith('```json')) {
        if (currentText.length > 0) {
            blocks.push({ type: 'text', content: currentText.join('\n') });
            currentText = [];
        }
        inJsonBlock = true;
        continue;
    }
    if (inJsonBlock && line.trim().startsWith('```')) {
        inJsonBlock = false;
        if (currentJson.length > 0) {
            blocks.push({ type: 'json', content: currentJson.join('\n') });
            currentJson = [];
        }
        continue;
    }

    if (inJsonBlock) {
        currentJson.push(line);
        continue;
    }

    // Tratamento Padrão de Markdown Tables
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (currentText.length > 0) {
        blocks.push({ type: 'text', content: currentText.join('\n') });
        currentText = [];
      }
      currentTable.push(line);
    } else {
      if (currentTable.length > 0) {
        blocks.push({ type: 'table', content: currentTable.join('\n') });
        currentTable = [];
      }
      currentText.push(line);
    }
  }

  if (currentText.length > 0) blocks.push({ type: 'text', content: currentText.join('\n') });
  if (currentTable.length > 0) blocks.push({ type: 'table', content: currentTable.join('\n') });
  if (currentJson.length > 0) blocks.push({ type: 'json', content: currentJson.join('\n') });
  if (currentImages.length > 0) blocks.push({ type: 'images', content: currentImages.join('\n') });

  // Renderiza uma linha que pode conter: **bold**, ![alt](url), e texto puro
  const renderLine = (line, key) => {
    const TOKEN_RE = /(\*\*.*?\*\*|!\[([^\]]*)\]\((https?:\/\/[^)]+)\))/g;
    const parts = [];
    let last = 0;
    let match;
    while ((match = TOKEN_RE.exec(line)) !== null) {
      if (match.index > last) parts.push({ type: 'text', value: line.slice(last, match.index) });
      if (match[0].startsWith('**')) {
        parts.push({ type: 'bold', value: match[0].slice(2, -2) });
      } else {
        parts.push({ type: 'img', alt: match[2], src: match[3] });
      }
      last = match.index + match[0].length;
    }
    if (last < line.length) parts.push({ type: 'text', value: line.slice(last) });

    return (
      <React.Fragment key={key}>
        {parts.map((part, pi) => {
          if (part.type === 'bold') return <strong key={pi}>{part.value}</strong>;
          if (part.type === 'img') return (
            <img
              key={pi}
              src={part.src}
              alt={part.alt}
              style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', margin: '4px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          );
          return <span key={pi}>{part.value}</span>;
        })}
      </React.Fragment>
    );
  };

  const renderText = (str) => {
    const lines = str.split('\n');
    return (
      <React.Fragment>
        {lines.map((l, j) => (
          <React.Fragment key={j}>
            {renderLine(l, j)}
            {j !== lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  return (
    <>
      {blocks.map((block, idx) =>
        block.type === 'table' ? (
           <React.Fragment key={idx}>
              {parseMarkdownTable(block.content)}
           </React.Fragment>
        ) : block.type === 'json' ? (
           <React.Fragment key={idx}>
              {parseJsonTable(block.content)}
           </React.Fragment>
        ) : block.type === 'images' ? (
           <React.Fragment key={idx}>
              {parseImagesBlock(block.content)}
           </React.Fragment>
        ) : (
           <React.Fragment key={idx}>
              {renderText(block.content)}
           </React.Fragment>
        )
      )}
    </>
  );
};

const STEP_LABELS = {
  interpreting: 'Interpretando pergunta...',
  querying:     'Consultando banco de dados...',
  narrative:    'Gerando narrativa...',
  done:         'Resposta pronta!',
};

// ── Formata data de atualização da conversa para exibição ───────────────────
const fmtDataConversa = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (sameDay(d, hoje)) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (sameDay(d, ontem)) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};

// ── Item de conversa na sidebar ─────────────────────────────────────────────
const ConversaItem = ({ conversa, ativa, onSelect, onRename, onDelete }) => {
  const [editando, setEditando] = useState(false);
  const [tituloEdit, setTituloEdit] = useState(conversa.titulo || 'Nova conversa');
  const inputEditRef = useRef(null);

  useEffect(() => {
    if (editando) {
      inputEditRef.current?.focus();
      inputEditRef.current?.select();
    }
  }, [editando]);

  const confirmarRename = () => {
    const novo = tituloEdit.trim();
    if (novo && novo !== conversa.titulo) {
      onRename(conversa.id, novo);
    } else {
      setTituloEdit(conversa.titulo || 'Nova conversa');
    }
    setEditando(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') confirmarRename();
    if (e.key === 'Escape') { setTituloEdit(conversa.titulo || 'Nova conversa'); setEditando(false); }
  };

  return (
    <div
      className={`${styles.conversaItem} ${ativa ? styles.conversaItemAtiva : ''}`}
      onClick={() => !editando && onSelect(conversa.id)}
      title={conversa.titulo || 'Nova conversa'}
    >
      <FiMessageSquare className={styles.conversaIcon} />
      <div className={styles.conversaInfo}>
        {editando ? (
          <input
            ref={inputEditRef}
            className={styles.conversaEditInput}
            value={tituloEdit}
            onChange={e => setTituloEdit(e.target.value)}
            onBlur={confirmarRename}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>
            <span className={styles.conversaTitulo}>
              {conversa.titulo || 'Nova conversa'}
            </span>
            <span className={styles.conversaData}>
              {fmtDataConversa(conversa.atualizado_em)}
            </span>
          </>
        )}
      </div>
      <div className={styles.conversaAcoes} onClick={e => e.stopPropagation()}>
        <button
          className={styles.conversaAcaoBtn}
          title="Renomear"
          onClick={() => setEditando(true)}
        >
          <FiEdit2 size={12} />
        </button>
        <button
          className={`${styles.conversaAcaoBtn} ${styles.conversaAcaoBtnDelete}`}
          title="Excluir"
          onClick={() => onDelete(conversa.id)}
        >
          <FiTrash2 size={12} />
        </button>
      </div>
    </div>
  );
};

// ── Sidebar de conversas ─────────────────────────────────────────────────────
const SidebarConversas = ({ conversas, conversaIdAtiva, onSelect, onNova, onRename, onDelete }) => (
  <div className={styles.sidebar}>
    <div className={styles.sidebarHeader}>
      <span className={styles.sidebarTitulo}>Conversas</span>
      <button className={styles.novaBtnSidebar} onClick={onNova} title="Nova conversa">
        <FiPlus size={16} />
        <span>Nova</span>
      </button>
    </div>
    <div className={styles.sidebarLista}>
      {conversas.length === 0 ? (
        <div className={styles.sidebarVazio}>Nenhuma conversa ainda.</div>
      ) : (
        conversas.map(c => (
          <ConversaItem
            key={c.id}
            conversa={c}
            ativa={c.id === conversaIdAtiva}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  </div>
);

const ChatAssistant = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [lojaAtiva, setLojaAtiva] = useState('todas');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchingHistory, setFetchingHistory] = useState(true);

  // ── Estado multi-conversa ──
  const [conversas, setConversas] = useState([]);
  const [conversaIdAtiva, setConversaIdAtiva] = useState(null);
  // null = tela de welcome (nova conversa ainda não criada)

  // Sidebar mobile
  const [showSidebarMobile, setShowSidebarMobile] = useState(false);

  // Histórico pessoal de perguntas (Offcanvas lateral direito)
  const [showHistory, setShowHistory] = useState(false);
  const [userLogs, setUserLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Feedback por mensagem: { [msgIdx]: 'positivo' | 'negativo' }
  const [feedbacks, setFeedbacks] = useState({});
  // Passo atual do streaming SSE
  const [streamStep, setStreamStep] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Scroll para o fim ──────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // ── Carregar lista de conversas no mount ───────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetchConversas();
        if (res.success && res.conversas && res.conversas.length > 0) {
          setConversas(res.conversas);
          // Carrega a conversa mais recente (primeira da lista — ordenada por recência)
          const maisRecente = res.conversas[0];
          const detalhe = await fetchConversa(maisRecente.id);
          if (detalhe.success && detalhe.conversa) {
            setMessages(detalhe.conversa.mensagens || []);
            setConversaIdAtiva(maisRecente.id);
          }
        }
        // Se não há conversas: permanece com messages=[] e conversaIdAtiva=null (welcome)
      } catch (e) {
        console.error('Falha ao carregar conversas:', e);
      } finally {
        setFetchingHistory(false);
      }
    };
    init();
  }, []);

  // ── Trocar de conversa ─────────────────────────────────────────────────────
  const handleSelectConversa = useCallback(async (id) => {
    if (id === conversaIdAtiva || isLoading) return;
    setFeedbacks({});
    try {
      const res = await fetchConversa(id);
      if (res.success && res.conversa) {
        setMessages(res.conversa.mensagens || []);
        setConversaIdAtiva(id);
        setShowSidebarMobile(false);
      }
    } catch (e) {
      console.error('Erro ao carregar conversa:', e);
    }
  }, [conversaIdAtiva, isLoading]);

  // ── Nova conversa (lazy — cria na 1ª mensagem) ─────────────────────────────
  const handleNovaConversa = useCallback(() => {
    if (isLoading) return;
    setMessages([]);
    setConversaIdAtiva(null);
    setFeedbacks({});
    setShowSidebarMobile(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isLoading]);

  // ── Renomear conversa ──────────────────────────────────────────────────────
  const handleRenameConversa = useCallback(async (id, novoTitulo) => {
    try {
      await renameConversa(id, novoTitulo);
      setConversas(prev => prev.map(c => c.id === id ? { ...c, titulo: novoTitulo } : c));
    } catch (e) {
      console.error('Erro ao renomear:', e);
    }
  }, []);

  // ── Excluir conversa ───────────────────────────────────────────────────────
  const handleDeleteConversa = useCallback(async (id) => {
    if (!window.confirm('Excluir esta conversa permanentemente?')) return;
    try {
      await deleteConversa(id);
      const novaLista = conversas.filter(c => c.id !== id);
      setConversas(novaLista);
      if (id === conversaIdAtiva) {
        // Vai para a mais recente restante, ou welcome
        if (novaLista.length > 0) {
          const res = await fetchConversa(novaLista[0].id);
          if (res.success && res.conversa) {
            setMessages(res.conversa.mensagens || []);
            setConversaIdAtiva(novaLista[0].id);
          }
        } else {
          setMessages([]);
          setConversaIdAtiva(null);
        }
        setFeedbacks({});
      }
    } catch (e) {
      console.error('Erro ao excluir:', e);
    }
  }, [conversas, conversaIdAtiva]);

  // ── Feedback ───────────────────────────────────────────────────────────────
  const handleFeedback = async (msgIdx, rating, msg) => {
    if (feedbacks[msgIdx]) return;
    setFeedbacks(prev => ({ ...prev, [msgIdx]: rating }));
    const allMsgs = messages.filter(m => m.role === 'user' || m.role === 'assistant');
    const pergunta = allMsgs.slice(0, msgIdx).reverse().find(m => m.role === 'user')?.content || '';
    await sendIAFeedback({ rating, pergunta, log_id: msg.log_id || null });
  };

  // ── Histórico pessoal ──────────────────────────────────────────────────────
  const handleOpenHistory = async () => {
    setShowHistory(true);
    setLoadingLogs(true);
    const res = await fetchIAUserLogs();
    if (res && res.success) setUserLogs(res.logs);
    setLoadingLogs(false);
  };

  // ── Input ──────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = 'inherit';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
  };

  const clearInput = () => {
    setInputValue('');
    if (inputRef.current) inputRef.current.style.height = 'inherit';
  };

  // ── Enviar mensagem ────────────────────────────────────────────────────────
  const handleSendMessage = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // Monta histórico para a API (sem mensagens de sistema)
    const chatHistoryForAPI = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }));

    const newUserMessage = { role: 'user', content: trimmedInput };
    setMessages(prev => [...prev, newUserMessage]);
    clearInput();
    setIsLoading(true);
    setStreamStep('interpreting');

    chatHistoryForAPI.push(newUserMessage);

    // Lazy create: se não há conversaIdAtiva, cria agora antes de enviar
    let idParaEnviar = conversaIdAtiva;
    const eraPrimeiraMsg = !conversaIdAtiva;
    if (!idParaEnviar) {
      try {
        const criada = await createConversa();
        if (criada.success && criada.id) {
          idParaEnviar = criada.id;
          setConversaIdAtiva(criada.id);
        }
      } catch (e) {
        console.error('Erro ao criar conversa:', e);
        // Continua sem conversaId (cai no fluxo legado do backend)
      }
    }

    const handleResponse = async (res) => {
      setStreamStep(null);
      if (res && res.success && res.response) {
        const novaMensagem = {
          role: 'assistant',
          content: res.response.content || 'Não consegui formular uma resposta textual final.',
          log_id: res.log_id || null
        };
        if (res.xlsxDownload) novaMensagem.xlsxDownload = res.xlsxDownload;
        setMessages(prev => [...prev, novaMensagem]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '**Desculpe**, ocorreu um erro ao se comunicar com o servidor de IA.\n\nVerifique as chaves e configuração no Terminal.'
        }]);
      }
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 10);

      // Após a resposta: atualiza a lista de conversas pra pegar o título gerado pelo backend
      try {
        const resLista = await fetchConversas();
        if (resLista.success && resLista.conversas) {
          setConversas(resLista.conversas);
          // Se era 1ª msg e acabamos de criar a conversa, garantir que está na lista como ativa
          if (eraPrimeiraMsg && idParaEnviar) {
            setConversaIdAtiva(idParaEnviar);
          }
        }
      } catch (e) {
        // Não crítico
      }
    };

    // Tenta streaming SSE; se falhar, cai no fetch normal
    try {
      sendChatToIAStream(chatHistoryForAPI, lojaAtiva, {
        conversaId: idParaEnviar,
        onProgress: (event) => {
          if (event.step && STEP_LABELS[event.step]) setStreamStep(event.step);
        },
        onDone: handleResponse,
        onError: async (err) => {
          console.warn('[Stream] Fallback para fetch padrão:', err.message);
          try {
            const res = await sendChatToIA(chatHistoryForAPI, lojaAtiva, idParaEnviar);
            handleResponse(res);
          } catch (e2) {
            setMessages(prev => [...prev, { role: 'assistant', content: `**Erro:** ${e2.message}` }]);
            setIsLoading(false);
            setStreamStep(null);
          }
        }
      });
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Ocorreu um erro ao enviar.**\n\n${error.message}`
      }]);
      setIsLoading(false);
      setStreamStep(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ── Loading inicial ────────────────────────────────────────────────────────
  if (fetchingHistory) {
    return (
      <div className="text-center mt-5 text-dark">
        <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
        Carregando conversas...
      </div>
    );
  }

  // ── Render principal ───────────────────────────────────────────────────────
  return (
    <div className={styles.chatRoot}>

      {/* SIDEBAR DESKTOP */}
      <SidebarConversas
        conversas={conversas}
        conversaIdAtiva={conversaIdAtiva}
        onSelect={handleSelectConversa}
        onNova={handleNovaConversa}
        onRename={handleRenameConversa}
        onDelete={handleDeleteConversa}
      />

      {/* SIDEBAR MOBILE — Offcanvas */}
      <Offcanvas
        show={showSidebarMobile}
        onHide={() => setShowSidebarMobile(false)}
        placement="start"
        className="bg-white border-end"
        style={{ width: '280px', maxWidth: '85vw' }}
      >
        <Offcanvas.Header closeButton className="border-bottom py-2">
          <Offcanvas.Title className="fs-6 fw-bold text-dark d-flex align-items-center gap-2">
            <FiMessageSquare size={16} /> Conversas
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className={styles.sidebarOffcanvasNova}>
            <button className={styles.novaBtnSidebar} onClick={handleNovaConversa}>
              <FiPlus size={16} />
              <span>Nova conversa</span>
            </button>
          </div>
          <div className={styles.sidebarLista}>
            {conversas.length === 0 ? (
              <div className={styles.sidebarVazio}>Nenhuma conversa ainda.</div>
            ) : (
              conversas.map(c => (
                <ConversaItem
                  key={c.id}
                  conversa={c}
                  ativa={c.id === conversaIdAtiva}
                  onSelect={handleSelectConversa}
                  onRename={handleRenameConversa}
                  onDelete={handleDeleteConversa}
                />
              ))
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* ÁREA PRINCIPAL DO CHAT */}
      <div className={styles.chatContainer}>
        <div className={styles.chatAreaWrapper}>

          {/* Cabecalho de Filtros */}
          <div className={`${styles.filterBar} d-flex justify-content-between p-2 border-bottom align-items-center flex-wrap gap-2`}>
            <div className="d-flex align-items-center gap-2">
              {/* Botão menu mobile */}
              <button
                className={`${styles.mobileSidebarToggle} d-lg-none btn btn-sm btn-outline-secondary`}
                onClick={() => setShowSidebarMobile(true)}
                title="Ver conversas"
              >
                <FiMenu size={16} />
              </button>
              <button className={`btn btn-sm ${lojaAtiva === 'todas' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setLojaAtiva('todas')} title="Ver Grupo Inteiro">Rede Gringao</button>
              <button className={`btn btn-sm ${lojaAtiva === 'ecommerce' ? 'btn-info' : 'btn-outline-secondary'}`} onClick={() => setLojaAtiva('ecommerce')} title="Focar no Site">E-commerce</button>
              <button className={`btn btn-sm ${lojaAtiva === 'centro' ? 'btn-warning text-dark' : 'btn-outline-secondary'}`} onClick={() => setLojaAtiva('centro')} title="Focar em Madureira">Centro</button>
              <button className={`btn btn-sm ${lojaAtiva === 'caxias' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setLojaAtiva('caxias')} title="Focar em DC">Caxias</button>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1 opacity-75"
                style={{ fontSize: '0.85rem' }}
                onClick={handleOpenHistory}
                title="Diario de perguntas"
              >
                <FiClock /> Historico
              </button>
            </div>
          </div>

          {/* Lista de mensagens */}
          <div className={styles.messagesList}>
            {messages.filter(m => m.role === 'user' || m.role === 'assistant').length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}><RoboAvatar size={56} /></div>
                <h2>Assistente Gringao IA</h2>
                <p>Pergunte-me qualquer coisa sobre os dados. Eu exporto listagens secretas extensas caso precise!</p>
              </div>
            ) : (
              messages.filter(m => m.role === 'user' || m.role === 'assistant').map((msg, idx) => (
                <div
                  key={idx}
                  className={`${styles.messageWrapper} ${msg.role === 'user' ? styles.user : styles.ai}`}
                >
                  <div className={`${styles.avatar} ${msg.role === 'user' ? styles.user : styles.ai}`}>
                    {msg.role === 'user' ? <FiUser /> : <RoboAvatar size={18} />}
                  </div>
                  <div className={styles.messageContent}>
                    {msg.role === 'assistant' ? (
                       <>
                         <FormattedMessage text={msg.content} />
                         {msg.xlsxDownload && (
                           <div className={styles.xlsxDownloadCard}>
                             <div className={styles.xlsxIcon}><FiFileText /></div>
                             <div className={styles.xlsxInfo}>
                               <span className={styles.xlsxTitle}>
                                 Planilha com {msg.xlsxDownload.totalRegistros.toLocaleString('pt-BR')} registros
                               </span>
                               <span className={styles.xlsxSubtitle}>{msg.xlsxDownload.fileName}</span>
                             </div>
                             <button
                               onClick={() => downloadIAXlsx(msg.xlsxDownload.fileId, msg.xlsxDownload.fileName)}
                               className={styles.xlsxButton}
                             >
                               <FiDownload /> Baixar Excel
                             </button>
                           </div>
                         )}
                         {/* Feedback */}
                         {idx > 0 && (
                           <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                             {feedbacks[idx] ? (
                               <span style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>
                                 {feedbacks[idx] === 'positivo' ? 'Obrigado pelo feedback!' : 'Vou melhorar!'}
                               </span>
                             ) : (
                               <>
                                 <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Esta resposta foi util?</span>
                                 <button
                                   onClick={() => handleFeedback(idx, 'positivo', msg)}
                                   title="Resposta util"
                                   style={{ background: 'none', border: '1px solid #334155', borderRadius: '6px', padding: '2px 7px', cursor: 'pointer', color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1.5 }}
                                   onMouseEnter={e => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.color = '#22c55e'; }}
                                   onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                                 >
                                   <FiThumbsUp size={12} />
                                 </button>
                                 <button
                                   onClick={() => handleFeedback(idx, 'negativo', msg)}
                                   title="Resposta ruim"
                                   style={{ background: 'none', border: '1px solid #334155', borderRadius: '6px', padding: '2px 7px', cursor: 'pointer', color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1.5 }}
                                   onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                                   onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                                 >
                                   <FiThumbsDown size={12} />
                                 </button>
                               </>
                             )}
                           </div>
                         )}
                       </>
                    ) : (
                       msg.content
                    )}
                  </div>
                </div>
              ))
            )}

            {isLoading && (
              <div className={`${styles.messageWrapper} ${styles.ai}`}>
                <div className={`${styles.avatar} ${styles.ai}`}>
                  <RoboAvatar size={18} />
                </div>
                <div className={styles.messageContent}>
                  <div className={styles.typingIndicator}>
                    <div className={styles.spinnerIA}></div>
                    {streamStep && STEP_LABELS[streamStep] && (
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: '10px', fontStyle: 'italic' }}>
                        {STEP_LABELS[streamStep]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <textarea
                ref={inputRef}
                className={styles.inputField}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="O que esta acontecendo nas vendas deste mes?"
                disabled={isLoading}
                rows={1}
              />
              <button
                className={styles.sendButton}
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                title="Enviar mensagem para IA Gringao"
              >
                <FiSend className={styles.sendButtonIcon} />
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Offcanvas Historico de Perguntas (direita) */}
      <Offcanvas show={showHistory} onHide={() => setShowHistory(false)} placement="end" className="bg-white text-dark border-start" style={{width: '320px'}}>
        <Offcanvas.Header closeButton className="border-bottom bg-light">
          <Offcanvas.Title className="fs-6 d-flex align-items-center gap-2 fw-bold text-success">
            <FiClock /> Diario de Perguntas
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0" style={{overflowY: 'auto'}}>
          <div className="p-3 text-muted border-bottom" style={{fontSize: '0.75rem', backgroundColor: '#f8f9fa'}}>
            Seu rastro na Assistente. Clique em uma anotacao antiga para refaze-la mais rapido.
          </div>
          {loadingLogs ? (
            <div className="text-center p-4"><div className="spinner-border spinner-border-sm text-secondary"></div></div>
          ) : userLogs?.length > 0 ? (
            <div className="px-2 py-3 d-flex flex-column gap-2">
              {userLogs.map(log => (
                <div key={log.id}
                  className="p-2 rounded shadow-sm"
                  style={{cursor:'pointer', backgroundColor: '#ffffff', border: '1px solid #dee2e6', borderLeft: '4px solid #198754'}}
                  onClick={() => {
                    setInputValue(log.pergunta);
                    setShowHistory(false);
                    setTimeout(() => inputRef.current?.focus(), 150);
                  }}
                >
                  <div className="d-flex justify-content-between text-muted" style={{fontSize: '0.65rem', marginBottom: '6px'}}>
                    <span>{log.ts}</span>
                    <span className="text-uppercase text-primary opacity-75 fw-bold">{log.filtro_loja}</span>
                  </div>
                  <div style={{fontSize: '0.8rem', lineHeight: '1.4', color: '#212529'}}>{log.pergunta}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-muted" style={{fontSize: '0.8rem'}}>Voce ainda nao fez perguntas a IA.</div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

    </div>
  );
};

export default ChatAssistant;
